import React, { useEffect, useState } from "react";
import { useScroll, motion } from "framer-motion";
import { api } from "@/lib/api";
import { initMetaPixel } from "@/utils/metaPixel";
import { FixedVideoBackground } from "@/components/landing/FixedVideoBackground";
import { ThreeBackground } from "@/components/landing/ThreeBackground";
import { Hero } from "@/components/landing/Hero";
import { SectionShell } from "@/components/landing/SectionShell";
import { ParallaxDivider } from "@/components/landing/ParallaxDivider";
import { ProofBand } from "@/components/landing/ProofBand";
import { AudienceSplit } from "@/components/landing/AudienceSplit";
import { InstagramShowcase } from "@/components/landing/InstagramShowcase";
import { ConsultationWizard } from "@/components/landing/ConsultationWizard";
import { AboutSection } from "@/components/landing/AboutSection";
import { ConsentBanner } from "@/components/landing/ConsentBanner";
import { SiteFooter } from "@/components/landing/Footer";
import { StickyCTA } from "@/components/landing/StickyCTA";

const scrollToConsult = () => {
  const el = document.getElementById("consult");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const captureUTM = () => {
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
  };
  if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
    sessionStorage.setItem("hs_utm", JSON.stringify(utm));
  }
  return utm;
};

const runTracking = (utm) => {
  // Only runs AFTER explicit consent (DPDP Act 2023)
  initMetaPixel();
  if (sessionStorage.getItem("hs_tracked")) return;
  let sessionKey = localStorage.getItem("hs_session_key");
  if (!sessionKey) {
    sessionKey = `sk_${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem("hs_session_key", sessionKey);
  }
  api
    .post("/track", {
      path: window.location.pathname,
      referrer: document.referrer || "",
      ...utm,
      session_key: sessionKey,
    })
    .then(() => sessionStorage.setItem("hs_tracked", "1"))
    .catch(() => {});
};

export default function LandingPage() {
  const { scrollYProgress } = useScroll();
  const [audienceHint, setAudienceHint] = useState("");

  useEffect(() => {
    const utm = captureUTM();
    // Analytics + Meta Pixel are consent-gated per DPDP Act 2023
    if (localStorage.getItem("hs_privacy_consent") === "accepted") {
      runTracking(utm);
    }
  }, []);

  const handleConsentDecision = (choice) => {
    if (choice === "accepted") {
      const utm = JSON.parse(sessionStorage.getItem("hs_utm") || "{}");
      runTracking(utm);
    }
  };

  const handleAudienceCTA = (hint) => {
    setAudienceHint(hint);
    scrollToConsult();
  };

  return (
    <div className="relative bg-[color:var(--hs-bg-0)] text-[color:var(--hs-ink)] overflow-x-clip" data-testid="landing-page">
      <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} />
      <div className="noise-overlay" aria-hidden="true" />

      {/* The showreel runs behind the ENTIRE page */}
      <FixedVideoBackground />
      <ThreeBackground />

      <div className="relative z-10">
        <Hero onPrimaryCTA={scrollToConsult} />

        <SectionShell testId="panel-proof">
          <ProofBand />
        </SectionShell>

        <ParallaxDivider text="From the scene. For the scene." testId="divider-1" />

        <SectionShell testId="panel-audience">
          <AudienceSplit onCTA={handleAudienceCTA} />
        </SectionShell>

        <ParallaxDivider text="Content · Campaigns · Growth" testId="divider-2" />

        <SectionShell testId="panel-instagram">
          <InstagramShowcase />
        </SectionShell>

        <ParallaxDivider text="No fluff. Just growth." testId="divider-3" />

        <SectionShell testId="panel-consult" className="!bg-[rgba(13,14,17,0.96)]">
          <ConsultationWizard audienceHint={audienceHint} />
        </SectionShell>

        <SectionShell testId="panel-about">
          <AboutSection />
        </SectionShell>

        <div className="mt-10">
          <SiteFooter />
        </div>
      </div>

      <StickyCTA onClick={scrollToConsult} />
      <ConsentBanner onDecision={handleConsentDecision} />
    </div>
  );
}
